/**
 * Procurements Service Hooks
 * React hooks for accessing procurement management services
 */
import { useCCMS } from '../useCCMS';
/**
 * Hook to access all procurement-related services
 */
export function useProcurementsServices() {
    const { services } = useCCMS();
    if (!services?.procurements) {
        throw new Error('Procurements services not available. Ensure CCMSProvider is configured with procurements services.');
    }
    return services.procurements;
}
/**
 * Hook to access PurchaseRequestService
 */
export function usePurchaseRequestService() {
    return useProcurementsServices().purchaseRequest;
}
/**
 * Hook to access QuotationService
 */
export function useQuotationService() {
    return useProcurementsServices().quotation;
}
/**
 * Hook to access InvoiceService
 */
export function useInvoiceService() {
    return useProcurementsServices().invoice;
}
/**
 * Hook to access RequestAttachmentService
 */
export function useRequestAttachmentService() {
    return useProcurementsServices().requestAttachment;
}
/**
 * Hook to access RequestCommentService
 */
export function useRequestCommentService() {
    return useProcurementsServices().requestComment;
}
/**
 * Hook to access RequestTypeService
 */
export function useRequestTypeService() {
    return useProcurementsServices().requestType;
}
/**
 * Hook to access RequestStatusService
 */
export function useRequestStatusService() {
    return useProcurementsServices().requestStatus;
}
/**
 * Hook to access RequestStageService
 */
export function useRequestStageService() {
    return useProcurementsServices().requestStage;
}
/**
 * Hook to access QuotationStatusService
 */
export function useQuotationStatusService() {
    return useProcurementsServices().quotationStatus;
}
/**
 * Hook to access InvoiceStatusService
 */
export function useInvoiceStatusService() {
    return useProcurementsServices().invoiceStatus;
}
/**
 * Hook to access ProcurementRoleTypeService
 */
export function useProcurementRoleTypeService() {
    return useProcurementsServices().procurementRoleType;
}
/**
 * Hook to access NotificationService
 */
export function useNotificationService() {
    return useProcurementsServices().notification;
}
/**
 * Hook to access AuditLogService
 */
export function useAuditLogService() {
    return useProcurementsServices().auditLog;
}
/**
 * Hook to access QuotationLineService
 */
export function useQuotationLineService() {
    return useProcurementsServices().quotationLine;
}
/**
 * Hook to access InvoiceLineService
 */
export function useInvoiceLineService() {
    return useProcurementsServices().invoiceLine;
}
/**
 * Hook to access RequestApprovalStatusService
 */
export function useRequestApprovalStatusService() {
    return useProcurementsServices().requestApprovalStatus;
}
/**
 * Hook to access CurrencyService
 */
export function useCurrencyService() {
    return useProcurementsServices().currency;
}
/**
 * Hook to access QuotationLineApprovalService
 */
export function useQuotationLineApprovalService() {
    return useProcurementsServices().quotationLineApproval;
}
/**
 * Hook to access StaffRequestPortalService
 */
export function useStaffRequestPortalService() {
    return useProcurementsServices().staffRequestPortal;
}
/**
 * Hook to access ApproverPortalService
 */
export function useApproverPortalService() {
    return useProcurementsServices().approverPortal;
}
/**
 * Hook to access FinancePortalService
 */
export function useFinancePortalService() {
    return useProcurementsServices().financePortal;
}
/**
 * Hook to access ProcurementDashboardService
 */
export function useProcurementDashboardService() {
    return useProcurementsServices().procurementDashboard;
}
/**
 * Hook to access ProcurementRoleAssignmentService
 */
export function useProcurementRoleAssignmentService() {
    return useProcurementsServices().procurementRoleAssignment;
}
/**
 * Hook to access RequestApprovalService
 */
export function useRequestApprovalService() {
    return useProcurementsServices().requestApproval;
}
/**
 * Hook to access RequestPurchaseOrderService
 */
export function useRequestPurchaseOrderService() {
    return useProcurementsServices().requestPurchaseOrder;
}
/**
 * Hook to access ProcurementItemService
 */
export function useProcurementItemService() {
    return useProcurementsServices().procurementItem;
}
/**
 * Hook to access ItemCategoryService
 */
export function useItemCategoryService() {
    return useProcurementsServices().itemCategory;
}
/**
 * Hook to access ApprovalLevelService
 */
export function useApprovalLevelService() {
    return useProcurementsServices().approvalLevel;
}
/**
 * Hook to access ApprovalPortalService (generic request-level approval)
 */
export function useApprovalPortalService() {
    return useProcurementsServices().approvalPortal;
}
/**
 * Hook to access QuotationLineApprovalStatusService (line-level approval status lookup)
 */
export function useQuotationLineApprovalStatusService() {
    return useProcurementsServices().quotationLineApprovalStatus;
}
/**
 * Hook to access ProcurementSettingService (system settings)
 */
export function useProcurementSettingService() {
    return useProcurementsServices().procurementSetting;
}
//# sourceMappingURL=useProcurementsServices.js.map